import { Injectable } from '@angular/core';
import { delay, Observable, of } from 'rxjs';
import { HttpEvent, HttpRequest, HttpResponse } from '@angular/common/http';
import {
  AdminBillingResponse,
  BillingManagementLink,
  BillingPayNowResult,
  BillingStatus,
  BillingStatusResponse,
  SubscriptionStatus,
} from '@app/core/models/billing.interface';

// Mock-mode handler for GET /:tenantId/billing. Returns a 3-days-left trial by default. To see the
// other banners and states while running `npm start` with mock data, set the status in the browser
// console and reload:
//   sessionStorage.setItem('mockBillingStatus', 'PAST_DUE')   // or ACTIVE, UNPAID, PAUSED, CANCELED
//   sessionStorage.setItem('mockBillingStatus', 'NONE')       // a workshop that is not billed
const STORAGE_KEY = 'mockBillingStatus';
const DAY_MS = 24 * 60 * 60 * 1000;

@Injectable({ providedIn: 'root' })
export class BillingMocksService {
  handleMockRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (req.method === 'GET' && req.url.endsWith('/billing')) {
      const body: BillingStatusResponse = { billing: this.mockBilling(this.chosenStatus()) };
      return of(new HttpResponse({ status: 200, body })).pipe(delay(400));
    }

    if (req.method === 'POST' && req.url.endsWith('/billing/management-link')) {
      // Stripe's pages do not exist in mock mode, so the management link just returns to this app's own Billing page.
      const body: BillingManagementLink = { url: `${window.location.origin}/app/billing` };
      return of(new HttpResponse({ status: 200, body })).pipe(delay(400));
    }

    if (req.method === 'POST' && req.url.endsWith('/billing/pay-now')) {
      // Paying clears the overdue state, so the next status read shows an active subscription.
      sessionStorage.setItem(STORAGE_KEY, 'ACTIVE');
      const body: BillingPayNowResult = { paid: true };
      return of(new HttpResponse({ status: 200, body })).pipe(delay(400));
    }

    throw new Error(`Unsupported mock billing request: ${req.method} ${req.url}`);
  }

  // Mock-mode handler for GET /admin/tenants/:tenantId/billing (the staff view). It follows the same
  // sessionStorage choice as the workshop view, so one switch changes both screens.
  handleAdminRequest(req: HttpRequest<any>): Observable<HttpEvent<any>> {
    if (req.method !== 'GET') {
      throw new Error(`Unsupported mock admin billing request: ${req.method} ${req.url}`);
    }
    const billing = this.mockBilling(this.chosenStatus());
    const body: AdminBillingResponse = {
      billing: billing && {
        ...billing,
        stripeCustomerId: 'cus_mock123',
        stripeSubscriptionId: 'sub_mock123',
        stripeMode: 'test',
        stripeCustomerUrl: 'https://dashboard.stripe.com/test/customers/cus_mock123',
        stripeSubscriptionUrl: 'https://dashboard.stripe.com/test/subscriptions/sub_mock123',
      },
    };
    return of(new HttpResponse({ status: 200, body })).pipe(delay(400));
  }

  private chosenStatus(): SubscriptionStatus | 'NONE' {
    return (
      (sessionStorage.getItem(STORAGE_KEY) as SubscriptionStatus | 'NONE' | null) ?? 'TRIALING'
    );
  }

  private mockBilling(status: SubscriptionStatus | 'NONE'): BillingStatus | null {
    if (status === 'NONE') return null;

    const now = Date.now();
    const inDays = (days: number) => new Date(now + days * DAY_MS).toISOString();
    const trialing = status === 'TRIALING';

    return {
      planKey: 'WORKSHOP',
      billingInterval: 'MONTH',
      status,
      access:
        status === 'PAST_DUE'
          ? 'FULL_WITH_WARNING'
          : trialing || status === 'ACTIVE'
            ? 'FULL'
            : 'LOCKED',
      trialEndsAt: trialing ? inDays(3) : null,
      currentPeriodEnd: inDays(trialing ? 3 : 24),
      cancelAtPeriodEnd: false,
      pendingPlanKey: null,
      pendingChangeAt: null,
    };
  }
}
